import { Checkbox, DefaultButton, Dropdown, IconButton, IDropdownOption, IOverflowSetItemProps, IPanelProps, IRenderFunction, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, ProgressIndicator, Separator, Stack, Text, } from "@fluentui/react";
import { RootState } from "app/store";
import CustomPanel from "components/controls/CustomPanel";
import ToggleOptions from "components/controls/ToggleOptions";
import { IToggleOption } from "components/controls/ToggleOptions/IToggleOption.type";
import { compareUnderWritingReviewToWithoutDispatch, fadeIn, getAccountRef, getSnapshotRef, panelButtonsStyles } from "global/Application";
import { useApplication } from "global/Application/Context";
import { EMergeVMStatus, TDefaultMergeOption } from "global/Compare/ComparisonViewModelGenerator";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { bcbs_groupconfigurationsnapshot as IXrmOptions_bcbs_groupconfigurationsnapshot } from "model/AttributeOptionSets.type";
import { bcbs_config_release, bcbs_groupconfigurationsnapshot } from "model/CRM";
import { IEntityReferenceValue, IOptionSetValue } from "model/FlexibleAttributeModel";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useSelector } from 'react-redux';
import SnapshotLookup from "../Controls/SnapshotLookup";


// ============================================================================
// Types
// ============================================================================
export enum ECompareConfigurationExpandOptions {
    collapsed = 'collapsed',
    differences = 'differences',
    all = 'all',
}


export interface ICompareConfiguration {
    leftSnapshotRef: IEntityReferenceValue | undefined;
    leftBlobVersion: string | undefined;
    rightSnapshotRef: IEntityReferenceValue | undefined;
    rightBlobVersion: string | undefined;
    accountRef: IEntityReferenceValue | undefined;
    output: string;
    release: string;
    tier: string;
    attribute: string;
    entities: string[];
    deactivated: string;
    fallback: string;
    pickVersions: boolean;
    useCurrentSnapshot: boolean;
    useCurrentAccount: boolean;
    mountingKey: number;
    loading: boolean;
    generateSingleSheet: boolean;
    underwritingReviewNecessary: boolean;
    affectsIDCards: boolean;
    targetedClaims: boolean;
    showDifferencesOnly: boolean;
    expandMode: ECompareConfigurationExpandOptions;
    displayInline: boolean;
    autoSelectRelevantAttributes: boolean;
    useReleaseID: string | null;
    rules: {
        mountLookupFilters: boolean;
        mountShowDiff: boolean;
        mountSingleSheetOption: boolean;
    }
    mergeReconciliation: boolean;
    defaultMergeAction: TDefaultMergeOption;
    mergePickMergeActionsAutoHide: boolean;
    mergeShowInstructions?: boolean;
}

enum ViewModelActionType {
    RESET,
    UPDATE,
    TOGGLE_ENTITIES,
    INCREMENT_MOUNTING_KEY,
}

type ViewModelAction =
    | { type: ViewModelActionType.RESET; payload: ICompareConfiguration }
    | { type: ViewModelActionType.UPDATE; payload: Partial<ICompareConfiguration> }
    | { type: ViewModelActionType.TOGGLE_ENTITIES; payload: string }
    | { type: ViewModelActionType.INCREMENT_MOUNTING_KEY };

export enum ECompareLookupDialogVariant {
    CompareExtractDialog,
    CompareAppDialog,
    UnderwritingReviewDialog,
    MergeAppDialog,
    PlanComparisonDialog,
    PharmacyDialog,
}

export interface IDialogResult {
    dismiss?: boolean;
}


interface ICompareLookupChildrenProps {
    variant: ECompareLookupDialogVariant;
    viewModel: ICompareConfiguration;
    dispatch: React.Dispatch<ViewModelAction>;
    dataSource: AppDataSource;
    state: RootState;
}

// ============================================================================
// Constants
// ============================================================================
const DEFAULT_ENTITIES = ['groupConfiguration', 'plan', 'benefit', 'accountReceivable', 'feesAndPremium', 'stopLoss'];

const EXPAND_OPTIONS: IToggleOption<ECompareConfigurationExpandOptions>[] = [
    { key: 'collapsed', text: "Collapsed", data: ECompareConfigurationExpandOptions.collapsed },
    { key: 'differences', text: "Expand Differences", data: ECompareConfigurationExpandOptions.differences },
    { key: 'all', text: "Expand All", data: ECompareConfigurationExpandOptions.all },
]

const MERGE_OPTIONS: IToggleOption<TDefaultMergeOption>[] = [
    { key: EMergeVMStatus.UseOrigin, text: "Prefer Origin", data: EMergeVMStatus.UseOrigin },
    { key: EMergeVMStatus.Pend, text: "Pending", data: EMergeVMStatus.Pend },
    { key: EMergeVMStatus.UseTarget, text: "Prefer Target", data: EMergeVMStatus.UseTarget },
]

const TITLE_MAP: Record<ECompareLookupDialogVariant, string> = {
    [ECompareLookupDialogVariant.CompareExtractDialog]: 'Compare Extract Configuration',
    [ECompareLookupDialogVariant.CompareAppDialog]: 'Compare Configuration',
    [ECompareLookupDialogVariant.MergeAppDialog]: 'Merge Configuration',
    [ECompareLookupDialogVariant.PlanComparisonDialog]: 'Plan Configuration',
    [ECompareLookupDialogVariant.PharmacyDialog]: 'Compare Configuration',
    [ECompareLookupDialogVariant.UnderwritingReviewDialog]: 'Underwriting Review Configuration',
};

// ============================================================================
// Reducer
// ============================================================================
const viewModelReducer = (state: ICompareConfiguration, action: ViewModelAction): ICompareConfiguration => {
    switch (action.type) {
        case ViewModelActionType.RESET:
            return action.payload;
        case ViewModelActionType.UPDATE:
            return { ...state, ...action.payload };
        case ViewModelActionType.TOGGLE_ENTITIES:
            return {
                ...state,
                entities: state.entities.includes(action.payload)
                    ? state.entities.filter(e => e !== action.payload)
                    : [...state.entities, action.payload],
            };
        case ViewModelActionType.INCREMENT_MOUNTING_KEY:
            return { ...state, mountingKey: state.mountingKey + 1 };
        default:
            return state;
    }
};

function resetViewModel(
    suggestedSnapshot: bcbs_groupconfigurationsnapshot | null | undefined,
    state: RootState,
    configuration: ECompareLookupDialogVariant
): ICompareConfiguration {

    const ret: ICompareConfiguration =
    {
        leftSnapshotRef: suggestedSnapshot ? getSnapshotRef(suggestedSnapshot) : undefined,
        leftBlobVersion: undefined,
        rightSnapshotRef: getSnapshotRef(state.data.snapshot),
        rightBlobVersion: undefined,
        useCurrentAccount: true,
        accountRef: getAccountRef(state.data.account!),
        output: 'excel',
        release: 'release',
        tier: 'default',
        attribute: 'masterAttribute',
        entities: [...DEFAULT_ENTITIES],
        deactivated: 'fieldByField',
        fallback: 'name',
        pickVersions: false,
        useCurrentSnapshot: false,
        mountingKey: 1,
        loading: false,
        generateSingleSheet: false,
        underwritingReviewNecessary: false,
        affectsIDCards: false,
        targetedClaims: false,
        showDifferencesOnly: configuration === ECompareLookupDialogVariant.CompareAppDialog ||
            configuration === ECompareLookupDialogVariant.UnderwritingReviewDialog ||
            configuration === ECompareLookupDialogVariant.MergeAppDialog ||
            configuration === ECompareLookupDialogVariant.PharmacyDialog
            ? true
            : false,
        expandMode: configuration === ECompareLookupDialogVariant.MergeAppDialog
            ? ECompareConfigurationExpandOptions.differences
            : ECompareConfigurationExpandOptions.differences,
        displayInline: true,
        autoSelectRelevantAttributes: false,
        useReleaseID: null,
        defaultMergeAction: EMergeVMStatus.UseTarget,
        mergeReconciliation: true,
        mergePickMergeActionsAutoHide: true,
        mergeShowInstructions: configuration === ECompareLookupDialogVariant.MergeAppDialog ? true : false,
        rules: {
            mountLookupFilters: configuration === ECompareLookupDialogVariant.CompareExtractDialog
                || configuration === ECompareLookupDialogVariant.CompareAppDialog
                || configuration === ECompareLookupDialogVariant.UnderwritingReviewDialog,
            mountSingleSheetOption: configuration === ECompareLookupDialogVariant.CompareExtractDialog,
            mountShowDiff: configuration === ECompareLookupDialogVariant.PharmacyDialog
                || configuration === ECompareLookupDialogVariant.CompareAppDialog
                || configuration === ECompareLookupDialogVariant.UnderwritingReviewDialog
                || configuration === ECompareLookupDialogVariant.MergeAppDialog,
        }
    }

    return ret;
}


// ============================================================================
// Sub-components
// ============================================================================
const CompareSettingsPanel = (props: ICompareLookupChildrenProps) => {

    return (
        <CustomPanel
            title="Compare Settings"
            canCollapse={true}
            defaultCollapsed={false}
        >
            <Stack tokens={{ childrenGap: '15px' }} styles={{ root: { marginTop: '5px', marginBottom: '10px' } }}>

                {props.viewModel.rules.mountShowDiff &&
                    <ToggleOptions
                        key={`expand-mode`}
                        label="Default Expand Mode"
                        defaultSelectedKey={props.viewModel.expandMode ?? ECompareConfigurationExpandOptions.differences}
                        clearHidden={true}
                        options={EXPAND_OPTIONS}
                        onChange={(_, option) => {

                            if (!option?.data) {
                                return;
                            }
                            const nextExpandMode = option.data as ECompareConfigurationExpandOptions;

                            props.dispatch({
                                type: ViewModelActionType.UPDATE,
                                payload: {
                                    expandMode: nextExpandMode,
                                },
                            });
                        }}
                    />
                }

                {props.viewModel.rules.mountSingleSheetOption &&
                    <Checkbox
                        label="Generate a single-sheet spreadsheet"
                        checked={!!props.viewModel.generateSingleSheet}
                        disabled={props.viewModel.loading}
                        onChange={(_, checked) => {
                            props.dispatch({
                                type: ViewModelActionType.UPDATE,
                                payload: {
                                    generateSingleSheet: checked ?? false
                                },
                            });
                        }}
                    />
                }

                {props.viewModel.rules.mountShowDiff &&
                    <Checkbox
                        label="Show Differences Only"
                        checked={props.viewModel.showDifferencesOnly}
                        onChange={(e, checked) => {
                            props.dispatch({
                                type: ViewModelActionType.UPDATE,
                                payload: { showDifferencesOnly: checked ?? false },
                            });
                        }}
                    />
                }


                {props.viewModel.rules.mountShowDiff &&
                    <Checkbox
                        label="Display Inline"
                        disabled={true}
                        checked={props.viewModel.displayInline}
                        onChange={(e, checked) => {
                            props.dispatch({
                                type: ViewModelActionType.UPDATE,
                                payload: { displayInline: checked ?? false },
                            });
                        }}
                    />
                }

                <Checkbox
                    label="Compare Properties, References, Tiers"
                    checked={true}
                    disabled={true}
                />
            </Stack>

        </CustomPanel>
    );
}

const MergeSettingsPanel: React.FC<ICompareLookupChildrenProps> = (props) => {

    const changeTypeOptions = useMemo(() => {
        const changeTypeOptions: IDropdownOption<IOptionSetValue<number>>[] = [];

        props.state.data.config.cache.options_bcbs_changetypecode.forEach(t => {
            const option: IDropdownOption<IOptionSetValue<number>> = { ...t! };
            changeTypeOptions.push(option);
        });


        return changeTypeOptions ?? [];

    }, [props.state.data.config.cache.options_bcbs_changetypecode]);


    return (
        <CustomPanel
            title="Merge Settings"
            canCollapse={true}
            defaultCollapsed={false}
        >
            <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { marginTop: '10px', marginBottom: '10px' } }}>
                <ToggleOptions
                    key={`merge-mode`}
                    label="Which side's values to prefer when there is a difference?"
                    defaultSelectedKey={props.viewModel.defaultMergeAction}
                    clearHidden={true}
                    options={MERGE_OPTIONS.filter(t => t.data !== EMergeVMStatus.Pend)}
                    onChange={(_, option) => {
                        if (option)
                            props.dispatch({
                                type: ViewModelActionType.UPDATE,
                                payload: {
                                    defaultMergeAction: option.data,
                                },
                            });

                    }}
                />

                <Dropdown
                    label={"Change Type"}
                    options={changeTypeOptions ?? []}
                    disabled={true}
                    selectedKey={IXrmOptions_bcbs_groupconfigurationsnapshot.bcbs_changetypecode["Comprehensive Change(808900004)"].value}
                />

                <Checkbox
                    label="Show Merge Instructions"
                    checked={props.viewModel.mergeShowInstructions}
                    onChange={(_, checked) => {
                        props.dispatch({
                            type: ViewModelActionType.UPDATE,
                            payload: {
                                mergeShowInstructions: checked ?? false,
                            },
                        });
                    }}
                />
                <Checkbox
                    label="Reconciliation"
                    checked={props.viewModel.mergeReconciliation}
                    onChange={(_, checked) => {
                        props.dispatch({
                            type: ViewModelActionType.UPDATE,
                            payload: {
                                mergeReconciliation: checked ?? false,
                            },
                        });
                    }}
                />

                <Checkbox
                    label="Deactivated entities should not be added/merged to target"
                    disabled={true}
                />


                <Checkbox
                    label="Cancelled entities should not be added/merged to target"
                    disabled={true}
                />

            </Stack>

        </CustomPanel>
    );
}

const AdvancedSettingsPanel = (props: ICompareLookupChildrenProps) => {

    const DROPDOWN_OPTIONS = useMemo(() => {
        const DROPDOWN_OPTIONS = {
            entities: [
                { key: 'groupConfiguration', text: 'Group Configuration' },
                { key: 'plan', text: 'Plan' },
                { key: 'benefit', text: 'Benefit' },
                { key: 'accountReceivable', text: 'Account Receivable' },
                { key: 'feesAndPremium', text: 'Fees And Premium' },
                { key: 'stopLoss', text: 'Stop Loss' },
            ] as IDropdownOption[],
            attribute: [
                { key: 'masterAttribute', text: 'By Master Attribute' },
                { key: 'attribute', text: 'By Attribute' },
            ] as IDropdownOption[],
            tier: [
                { key: 'default', text: 'By Default Level Name' },
                { key: 'user', text: 'By User Level Name' },
            ] as IDropdownOption[],
            release: [
                { key: 'release', text: 'Use Each Snapshot\'s Release' },
                { key: 'lastPublished', text: 'Use Latest Published Release' },
            ] as IDropdownOption[],
            deactivated: [
                { key: 'fieldByField', text: 'Compare Field By Field' },
                { key: 'blankDeactivated', text: 'Blank Fields Under' },
                { key: 'ignore', text: 'Ignore Compare If On Either Side' },
            ] as IDropdownOption[],
            output: [
                { key: 'excel', text: 'Excel Spreadsheet' },
                { key: 'html', text: 'Web Report/HTML' },
                { key: 'csv', text: 'CSV' },
            ] as IDropdownOption[],
        };

        return DROPDOWN_OPTIONS;
    }, []);


    return <CustomPanel
        title="Advanced Settings"
        canCollapse={true}
        defaultCollapsed={true}
    >
        <Stack tokens={{ childrenGap: '10px' }} styles={{ root: { marginTop: '10px', marginBottom: '10px' } }}>
            <Checkbox
                label="Use current BlueRelationship"
                checked={props.viewModel.useCurrentAccount}
                onChange={(_, checked) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: {
                            useCurrentAccount: checked ?? false,
                            accountRef: !props.viewModel.accountRef ? getAccountRef(props.state.data.account) : undefined,
                        },
                    });
                }}
            />

            {props.state.status.debug.admin && (
                <>
                    <Checkbox
                        label="Use current configuration"
                        checked={!!props.viewModel.useCurrentSnapshot}
                        onChange={(_, checked) => {
                            props.dispatch({
                                type: ViewModelActionType.UPDATE,
                                payload: {
                                    useCurrentSnapshot: checked ?? false,
                                    mountingKey: props.viewModel.mountingKey + 1,
                                },
                            });
                        }}
                    />
                    <Checkbox
                        label="Pick versions"
                        checked={!!props.viewModel.pickVersions}
                        onChange={(_, checked) => {
                            props.dispatch({
                                type: ViewModelActionType.UPDATE,
                                payload: {
                                    pickVersions: !props.viewModel.pickVersions,
                                    ...(props.viewModel.pickVersions && {
                                        leftBlobVersion: undefined,
                                        rightBlobVersion: undefined,
                                        mountingKey: props.viewModel.mountingKey + 1,
                                    }),
                                },
                            });
                        }}
                    />
                </>
            )}

            <Dropdown
                selectedKey={props.viewModel.output}
                label="Output"
                options={DROPDOWN_OPTIONS.output}
                disabled={true}
                onChange={(_, option) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: { output: option?.key as string },
                    });
                }}
            />

            <Dropdown
                label="Deactivated Context"
                selectedKey={props.viewModel.deactivated}
                disabled={true}
                options={DROPDOWN_OPTIONS.deactivated}
                onChange={(_, option) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: { deactivated: option?.key as string },
                    });
                }}
            />

            <Dropdown
                label="Entities"
                selectedKeys={props.viewModel.entities}
                disabled={true}
                multiSelect={true}
                options={DROPDOWN_OPTIONS.entities}
                onChange={(_, option) => {
                    if (option) {
                        props.dispatch({
                            type: ViewModelActionType.TOGGLE_ENTITIES,
                            payload: option.key as string,
                        });
                    }
                }}
            />

            <Dropdown
                label="Release"
                selectedKey={props.viewModel.release}
                disabled={false}
                options={DROPDOWN_OPTIONS.release}
                onChange={async (_, option) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: { loading: true },
                    });

                    try {
                        let latestPublishedReleaseId: bcbs_config_release | null = null;

                        if (option?.key === 'lastPublished') {
                            latestPublishedReleaseId = await props.dataSource.bcbs_config_releases_byPublishedRelease_getAsync()
                        }

                        props.dispatch({
                            type: ViewModelActionType.UPDATE,
                            payload: {
                                release: option?.key as string,
                                useReleaseID: latestPublishedReleaseId
                                    ? latestPublishedReleaseId.bcbs_config_releaseid
                                    : null,
                            },
                        });
                    } finally {
                        props.dispatch({
                            type: ViewModelActionType.UPDATE,
                            payload: { loading: false },
                        });
                    }
                }}
            />

            <Dropdown
                label="Tier Match"
                selectedKey={props.viewModel.tier}
                disabled={true}
                options={DROPDOWN_OPTIONS.tier}
                onChange={(_, option) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: { tier: option?.key as string },
                    });
                }}
            />

            <Dropdown
                label="Field Match"
                selectedKey={props.viewModel.attribute}
                disabled={true}
                options={DROPDOWN_OPTIONS.attribute}
                onChange={(_, option) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: { attribute: option?.key as string },
                    });
                }}
            />

            <Checkbox
                label="fallback to name, if not matched by id"
                checked={props.viewModel.fallback === 'name'}
                disabled={true}
                onChange={(_, checked) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: { fallback: checked ? 'name' : '' },
                    });
                }}
            />
        </Stack>
    </CustomPanel>;
};

const FilterSettingsPanel = (props: ICompareLookupChildrenProps) => {
    const showLatestReleaseNotice = props.viewModel.release !== 'lastPublished' &&
        (!!props.viewModel.affectsIDCards || !!props.viewModel.targetedClaims);

    return <CustomPanel
        title="Flexible Attribute Filters"
        canCollapse={true}
        defaultCollapsed={false}
    >
        <Stack tokens={{ childrenGap: '10px' }}>

            <Checkbox
                label="All Fields"
                checked={!props.viewModel.underwritingReviewNecessary && !props.viewModel.affectsIDCards && !props.viewModel.targetedClaims}
                disabled={props.viewModel.loading}
                onChange={(_, checked) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: {
                            underwritingReviewNecessary: !(checked ?? false),
                            affectsIDCards: !(checked ?? false),
                            targetedClaims: !(checked ?? false),
                        },
                    });
                }}
            />


            <Checkbox
                label="Underwriting Review Necessary fields"
                checked={!!props.viewModel.underwritingReviewNecessary}
                disabled={props.viewModel.loading}
                onChange={(_, checked) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: { underwritingReviewNecessary: checked ?? false },
                    });
                }}
            />

            <Checkbox
                label="Affects ID Cards fields"
                checked={!!props.viewModel.affectsIDCards}
                disabled={props.viewModel.loading}
                onChange={(_, checked) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: { affectsIDCards: checked ?? false },
                    });
                }}
            />

            <Checkbox
                label="Targeted Claims fields"
                checked={!!props.viewModel.targetedClaims}
                disabled={props.viewModel.loading}
                onChange={(_, checked) => {
                    props.dispatch({
                        type: ViewModelActionType.UPDATE,
                        payload: { targetedClaims: checked ?? false },
                    });
                }}
            />

            {showLatestReleaseNotice && (
                <MessageBar
                    messageBarType={MessageBarType.warning}
                    isMultiline={true}
                >
                    <Text variant="small">
                        Only snapshots on latest release support this filter, use Advanced Settings to override a release if needed.
                    </Text>
                </MessageBar>
            )}

        </Stack>
    </CustomPanel>
}

const BodyContainerPanel = (props: ICompareLookupChildrenProps) => {
    const [showEffectiveDateWarning, setShowEffectiveDateWarning] = useState(false);
    const isMergeApp = props.variant === ECompareLookupDialogVariant.MergeAppDialog;

    const isSwapDisabled = props.viewModel.loading
        || props.variant === ECompareLookupDialogVariant.PharmacyDialog
        || props.variant === ECompareLookupDialogVariant.UnderwritingReviewDialog;

    useEffect(() => {
        if (!isMergeApp) {
            setShowEffectiveDateWarning(false);
            return;
        }

        const originSnapshotId = props.viewModel.leftSnapshotRef?.id;
        const targetSnapshotId = props.viewModel.rightSnapshotRef?.id;

        if (!originSnapshotId || !targetSnapshotId) {
            setShowEffectiveDateWarning(false);
            return;
        }

        let isCancelled = false;

        const compareEffectiveDates = async () => {
            try {
                const [originSnapshot, targetSnapshot] = await Promise.all([
                    props.dataSource.getGroupConfigurationSnapshot(originSnapshotId, 'bcbs_effectivedate'),
                    props.dataSource.getGroupConfigurationSnapshot(targetSnapshotId, 'bcbs_effectivedate'),
                ]);

                const originTime = originSnapshot.bcbs_effectivedate
                    ? new Date(originSnapshot.bcbs_effectivedate).getTime()
                    : Number.NaN;
                const targetTime = targetSnapshot.bcbs_effectivedate
                    ? new Date(targetSnapshot.bcbs_effectivedate).getTime()
                    : Number.NaN;

                if (isCancelled) {
                    return;
                }

                if (Number.isNaN(originTime) || Number.isNaN(targetTime)) {
                    setShowEffectiveDateWarning(false);
                    return;
                }

                setShowEffectiveDateWarning(targetTime < originTime);
            } catch {
                if (!isCancelled) {
                    setShowEffectiveDateWarning(false);
                }
            }
        };

        compareEffectiveDates();

        return () => {
            isCancelled = true;
        };
    }, [
        isMergeApp,
        props.dataSource,
        props.viewModel.leftSnapshotRef?.id,
        props.viewModel.rightSnapshotRef?.id,
    ]);

    const onSwapSnapshots = useCallback(() => {
        if (isSwapDisabled) {
            return;
        }

        props.dispatch({
            type: ViewModelActionType.UPDATE,
            payload: {
                leftSnapshotRef: props.viewModel.rightSnapshotRef,
                leftBlobVersion: props.viewModel.rightBlobVersion,
                rightSnapshotRef: props.viewModel.leftSnapshotRef,
                rightBlobVersion: props.viewModel.leftBlobVersion,
                mountingKey: props.viewModel.mountingKey + 1,
            },
        });
    }, [
        isSwapDisabled,
        props.dispatch,
        props.viewModel.leftBlobVersion,
        props.viewModel.leftSnapshotRef,
        props.viewModel.mountingKey,
        props.viewModel.rightBlobVersion,
        props.viewModel.rightSnapshotRef,
    ]);

    const snapshotPanelItems = useMemo<IOverflowSetItemProps[]>(() => ([
        {
            key: 'swap-snapshots',
            name: 'Swap Origin and Target',
            onRender: () => (
                <IconButton
                    iconProps={{ iconName: 'Switch' }}
                    title="Swap Origin and Target"
                    ariaLabel="Swap Origin and Target"
                    disabled={isSwapDisabled}
                    onClick={(event) => {
                        event?.stopPropagation();
                        onSwapSnapshots();
                    }}
                />
            ),
        },
    ]), [isSwapDisabled, onSwapSnapshots]);

    return (
        <div className={fadeIn}>
            <Separator />
            <Stack tokens={{ childrenGap: '15px' }}>
                <CustomPanel
                    title="Snapshots"
                    canCollapse={true}
                    items={snapshotPanelItems}
                >
                    <Stack tokens={{ childrenGap: '20px' }} >
                        <SnapshotLookup
                            key={props.viewModel.mountingKey + 'A'}
                            disabled={props.viewModel.loading || props.variant === ECompareLookupDialogVariant.UnderwritingReviewDialog}
                            searchOnMount={false}
                            label="Origin Snapshot"
                            defaultValueAccount={props.viewModel.accountRef}
                            defaultValueSnapshot={props.viewModel.leftSnapshotRef}
                            defaultValueSnapshotVersion={props.viewModel.leftBlobVersion}
                            valueAccount={props.viewModel.useCurrentAccount ? props.viewModel.accountRef : undefined}
                            valueSnapshot={props.viewModel.useCurrentSnapshot ? props.viewModel.leftSnapshotRef : undefined}
                            searchVersion={props.viewModel.pickVersions}
                            onPick={(value, version) => {
                                if (value) {
                                    props.dispatch({
                                        type: ViewModelActionType.UPDATE,
                                        payload: { leftSnapshotRef: value, leftBlobVersion: version },
                                    });
                                }
                            }}
                            onCancel={() => {
                                props.dispatch({
                                    type: ViewModelActionType.INCREMENT_MOUNTING_KEY
                                });
                            }}
                        />

                        <SnapshotLookup
                            key={props.viewModel.mountingKey + 'B'}
                            searchOnMount={false}
                            label="Target Snapshot"
                            disabled={props.viewModel.loading || props.variant === ECompareLookupDialogVariant.PharmacyDialog || props.variant === ECompareLookupDialogVariant.UnderwritingReviewDialog}
                            defaultValueAccount={props.viewModel.accountRef}
                            defaultValueSnapshot={props.viewModel.rightSnapshotRef}
                            defaultValueSnapshotVersion={props.viewModel.rightBlobVersion}
                            valueAccount={props.viewModel.useCurrentAccount ? props.viewModel.accountRef : undefined}
                            valueSnapshot={props.viewModel.useCurrentSnapshot ? props.viewModel.rightSnapshotRef : undefined}
                            searchVersion={props.viewModel.pickVersions}
                            onPick={(value, version) => {
                                if (value) {
                                    props.dispatch({
                                        type: ViewModelActionType.UPDATE,
                                        payload: { rightSnapshotRef: value, rightBlobVersion: version },
                                    });
                                }
                            }}
                            onCancel={() => {
                                props.dispatch({
                                    type: ViewModelActionType.UPDATE,
                                    payload: {
                                        rightSnapshotRef: undefined,
                                        rightBlobVersion: undefined,
                                        mountingKey: props.viewModel.mountingKey + 1,
                                    },
                                });
                            }}
                        />

                        {isMergeApp && showEffectiveDateWarning && (
                            <MessageBar
                                messageBarType={MessageBarType.warning}
                                isMultiline={true}
                            >
                                <Stack tokens={{ childrenGap: '6px' }}>
                                    <Text>
                                        Target snapshot effective date is earlier than origin snapshot effective date.
                                    </Text>
                                    <Text variant="small">
                                        The merge process is intended for scenarios where the target snapshot has the most recent effective date and contains the latest overall configuration updates. Changes from the origin snapshot are then merged into the target snapshot.
                                    </Text>
                                </Stack>

                            </MessageBar>
                        )}

                    </Stack>
                </CustomPanel>

                {props.viewModel.rules.mountLookupFilters &&
                    <FilterSettingsPanel {...props} />
                }

                {props.variant === ECompareLookupDialogVariant.MergeAppDialog &&
                    <MergeSettingsPanel {...props} />
                }

                <CompareSettingsPanel {...props} />

                {props.variant === ECompareLookupDialogVariant.CompareExtractDialog &&
                    <AdvancedSettingsPanel {...props} />
                }

            </Stack>
        </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================
export interface ICompareLookupDialogProps {
    isOpen: boolean;
    viewModel?: ICompareConfiguration
    onDismiss?: (viewModel: ICompareConfiguration) => Promise<IDialogResult> | void;
    onContinue?: (viewModel: ICompareConfiguration) => Promise<IDialogResult>;
    variant: ECompareLookupDialogVariant;
    defaultOriginSnapshotId?: string;
}

const CompareLookupDialog = (props: ICompareLookupDialogProps) => {
    const application = useApplication();

    const state = useSelector((state: RootState) => state);

    const suggestedSnapshot = useMemo(() => {
        if (props.defaultOriginSnapshotId) {
            console.log('defaultOriginSnapshotId', props.defaultOriginSnapshotId);
            const snapshot = state?.status?.snapshots?.find(s => s.bcbs_groupconfigurationsnapshotid === props.defaultOriginSnapshotId);
            if (snapshot) {
                return snapshot;
            }
        }

        return compareUnderWritingReviewToWithoutDispatch(state, state.data.snapshot);
    }, [
        state,
        props.defaultOriginSnapshotId
    ]);

    const [viewModel, dispatch] = useReducer(viewModelReducer, null as any, () => resetViewModel(suggestedSnapshot, state, props.variant));

    useEffect(() => {
        // if (isOpen) {
        //     dispatch({ type: ViewModelActionType.RESET, payload: resetViewModel(suggestedSnapshot, state) });
        // }
    }, [props.isOpen, state, suggestedSnapshot]);

    const onDismissAsync = useCallback(async () => {
        await props.onDismiss?.(viewModel);
    }, [props.onDismiss, viewModel]);

    const onContinueAsync = useCallback(async () => {
        dispatch({
            type: ViewModelActionType.UPDATE,
            payload: { loading: true },
        });

        try {
            const result = await props.onContinue?.(viewModel);

            if (result?.dismiss)
                await onDismissAsync?.();
        } finally {
            dispatch({
                type: ViewModelActionType.UPDATE,
                payload: { loading: false },
            });
        }

    }, [dispatch, props.onContinue, onDismissAsync, viewModel]);

    const onRenderFooter: IRenderFunction<IPanelProps> = () => {
        let continueDisabled = false;

        if (viewModel.loading)
            continueDisabled = true;

        if (!viewModel.leftSnapshotRef)
            if (
                props.variant !== ECompareLookupDialogVariant.UnderwritingReviewDialog
                && props.variant !== ECompareLookupDialogVariant.CompareAppDialog
            )
                continueDisabled = true;

        if (!viewModel.rightSnapshotRef)
            continueDisabled = true;

        return (
            <Stack>
                {viewModel.loading &&
                    <ProgressIndicator label="Processing..." />
                }
                <Stack horizontal tokens={{ childrenGap: '10px' }} styles={{ root: { marginTop: '10px' } }}>
                    <PrimaryButton
                        text="Continue"
                        styles={panelButtonsStyles}
                        disabled={continueDisabled}
                        onClick={onContinueAsync}
                    />
                    {props.onDismiss &&
                        <DefaultButton
                            text="Cancel"
                            styles={panelButtonsStyles}
                            onClick={onDismissAsync}
                        />
                    }
                </Stack>
            </Stack>
        )
    };

    return (
        <Panel
            isOpen={props.isOpen}
            type={PanelType.medium}
            isLightDismiss={false}
            hasCloseButton={!!props.onDismiss}
            closeButtonAriaLabel="Close"
            headerText={TITLE_MAP[props.variant]}
            onRenderFooterContent={onRenderFooter}
            isFooterAtBottom={true}
            onDismiss={() => onDismissAsync()}
        >
            <BodyContainerPanel
                variant={props.variant}
                viewModel={viewModel}
                dispatch={dispatch}
                state={state}
                dataSource={application.dataSource}
            />
        </Panel>
    );
};

export default CompareLookupDialog;